import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Card, Row, Col, Badge, Button, Form, Alert } from "react-bootstrap";
import schoolApi from "../../api/schoolApi";
import moment from "moment";
import { Helmet } from "react-helmet";
import { toast, ToastContainer } from "react-toastify";
import { saveAs } from "file-saver";
import { usePermissions } from "../contexts/PermissionContext";
import ReviewTaskModal from "./ReviewTaskModal";
import { 
  Editor, 
  EditorProvider, 
  Toolbar, 
  BtnBold, 
  BtnItalic, 
  BtnUnderline, 
  BtnStrikeThrough, 
  Separator, 
  BtnNumberedList, 
  BtnBulletList, 
  BtnLink, 
  BtnClearFormatting, 
  BtnUndo, 
  BtnRedo 
} from "react-simple-wysiwyg";

const TaskDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userInfo } = usePermissions();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [file, setFile] = useState(null);
  const [submissionDescription, setSubmissionDescription] = useState("");
  const [reviewModalShow, setReviewModalShow] = useState(false);
  const [selectedUserForReview, setSelectedUserForReview] = useState(null);

  const isAdmin = userInfo?.userrole === "ADMIN" || userInfo?.userrole === "SYS_ADMIN";

  const isImage = (fileName) => {
    if (!fileName) return false;
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
  };

  const isVideo = (fileName) => {
    if (!fileName) return false;
    return /\.(mp4|webm|ogg)$/i.test(fileName);
  };

  useEffect(() => {
    fetchTaskDetails();
  }, [id]);

  useEffect(() => {
    if (task?.file_id && (isImage(task.file_title) || isVideo(task.file_title))) {
      const fetchPreview = async () => {
        try {
          const response = await schoolApi.downloadFiles(task.file_id);
          const blob = new Blob([response], { type: response.type });
          const url = URL.createObjectURL(blob);
          setImagePreview(url);
        } catch (error) {
          console.error("Error fetching preview:", error);
        }
      };
      fetchPreview();
    }
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [task?.file_id, task?.file_title]);

  const fetchTaskDetails = async () => {
    try {
      const result = await schoolApi.fetchTaskById(id);
      console.log("Fetched task details:", result);
      if (result.success) {
        setTask(result.result);
      }
    } catch (error) {
      console.error("Error fetching task details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileId = null, title = null) => {
    const idToDownload = fileId || task.file_id;
    if (!idToDownload) return;
    try {
      const response = await schoolApi.downloadFiles(idToDownload);
      const blob = new Blob([response], { type: response.type });
      saveAs(blob, title || task.file_title || "task-attachment");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  const handleIndividualStatusUpdate = async (userId, newStatus, adminComment = null) => {
    try {
      const result = await schoolApi.updateTaskUserStatus(id, userId, newStatus, null, null, adminComment);
      if (result.success) {
        toast.success(`User status updated to ${newStatus}`);
        fetchTaskDetails();
      }
    } catch (error) {
      toast.error("Error updating user status");
    }
  };

  const handleTeacherSubmit = async () => {
    if (task?.status === 'Cancelled') {
      toast.error("This task has been cancelled and cannot be submitted.");
      return;
    }
    if (!file) {
      toast.error("Please select a file to submit");
      return;
    }
    
    try {
      setLoading(true);
      // 1. Upload file
      const formData = new FormData();
      formData.append("file", file);
      const fileRes = await schoolApi.createFile(id, formData);
      
      if (fileRes.success) {
        // 2. Update assignment status with file_id and description
        const result = await schoolApi.updateTaskUserStatus(id, userInfo.id, 'Submitted', fileRes.arry[0].id, submissionDescription);
        if (result.success) {
          toast.success("Task submitted successfully");
          setFile(null);
          setSubmissionDescription("");
          fetchTaskDetails();
        }
      }
    } catch (error) {
      console.error("Error submitting task:", error);
      toast.error("Failed to submit task");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      const result = await schoolApi.updateTaskUserStatus(id, userInfo.id, newStatus);
      if (result.success) {
        toast.success(`Your assignment status updated to ${newStatus}`);
        fetchTaskDetails();
      }
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const handleGlobalStatusUpdate = async (newStatus) => {
    try {
      const payload = { ...task, status: newStatus, last_modified_by_id: userInfo.id };
      delete payload.assigned_users; // Remove virtual fields before sending
      delete payload.file_id;
      delete payload.file_title;

      const result = await schoolApi.updateTask(id, payload, null); // null means don't sync users
      if (result.success) {
        toast.success(`Task status updated to ${newStatus}`);
        fetchTaskDetails();
      }
    } catch (error) {
      toast.error("Error updating global task status");
    }
  };

  if (loading) return <div className="text-center mt-5">Loading task details...</div>;
  if (!task) return <div className="text-center mt-5">Task not found.</div>;

  const priorityColors = { High: 'danger', Medium: 'warning', Low: 'info' };
  const statusColors = {
    Pending: 'warning',
    'In Progress': 'info',
    Submitted: 'primary',
    Completed: 'success',
    Cancelled: 'secondary',
    Approved: 'success',
    Rejected: 'danger'
  };

  return (
    <Container className="mt-4 pb-5">
      <Helmet>
        <title>Task - {task.title}</title>
      </Helmet>
      <ToastContainer autoClose={2000} />
      
      <div className="mb-4 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)} className="me-3 rounded-circle shadow-sm border-0 bg-light">
            <i className="fa-solid fa-arrow-left text-dark"></i>
          </Button>
          <div>
            <Badge bg={priorityColors[task.priority]} className="mb-1">{task.priority?.toUpperCase()} PRIORITY</Badge>
            <h3 className="mb-0 fw-bold text-dark">{task.title}</h3>
          </div>
        </div>
        <Badge bg={statusColors[task.status]} className="px-4 py-2 fs-6 shadow-sm border-0">
          {task.status?.toUpperCase()}
        </Badge>
      </div>

      <Card className="shadow-sm border-0 mb-4 overflow-hidden rounded-4">
        <Card.Body className="p-0">
          <Row className="g-0">
            <Col lg={8} className="p-4 border-end bg-white">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0 d-flex align-items-center text-primary">
                  <i className="fa-solid fa-align-left me-2"></i>Task Description
                </h5>
                {task.file_id && task.file_id !== 'null' && (
                  <Button variant="outline-primary" size="sm" onClick={() => handleDownload()} className="rounded-pill px-3">
                    <i className="fa-solid fa-download me-2"></i>Download Attachment
                  </Button>
                )}
              </div>
              
              <div className="bg-light p-4 rounded-4 mb-4 shadow-sm" style={{ minHeight: '300px', border: '1px solid #eee' }} dangerouslySetInnerHTML={{ __html: task.description || "No description provided." }}>
              </div>

              {!isAdmin && imagePreview && (
                <div className="mt-4 border-0 rounded-4 p-3 bg-dark shadow-lg text-center overflow-hidden">
                  <div className="text-white small mb-3 opacity-75 fw-bold">MAIN ATTACHMENT PREVIEW</div>
                  {isImage(task.file_title) ? (
                    <img src={imagePreview} alt="Task attachment" className="img-fluid rounded-3 mx-auto d-block" style={{ maxHeight: '450px', objectFit: 'contain' }} />
                  ) : (
                    <video src={imagePreview} controls className="w-100 rounded-3" style={{ maxHeight: '450px' }} />
                  )}
                </div>
              )}
            </Col>

            <Col lg={4} className="p-4" style={{ backgroundColor: '#fcfcfd' }}>
              <div className="mb-4">
                <h6 className="fw-bold text-muted mb-3 border-bottom pb-2 text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>Timeline</h6>
                <div className="mb-3 p-3 bg-white rounded-3 border shadow-sm">
                  <div className="mb-2 d-flex justify-content-between align-items-center">
                    <span className="text-muted small">Assigned On:</span>
                    <span className="small fw-bold">{moment(task.assign_date).format('DD MMM, YYYY | hh:mm A')}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted small">Due Deadline:</span>
                    <span className="small fw-bold text-danger">{moment(task.due_date).format('DD MMM, YYYY | hh:mm A')}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white rounded-4 border shadow-sm">
                <h6 className="fw-bold mb-3 text-primary border-bottom pb-2 d-flex align-items-center">
                  <i className="fa-solid fa-cloud-arrow-up me-2"></i>Your Action
                </h6>
                {(() => {
                  let users = task.assigned_users;
                  if (typeof users === 'string') {
                    try { users = JSON.parse(users); } catch (e) { users = []; }
                  }
                  const myAssignment = users?.find(u => u.user_id === userInfo.id);
                  if (!myAssignment) return <div className="text-center text-muted small py-4 bg-light rounded-3 border-dashed">No individual submission required.</div>;

                  const isCancelled = task.status === 'Cancelled';
                  const isSubmitted = myAssignment.status === 'Submitted' || myAssignment.status === 'Approved';
                  const isRejected = myAssignment.status === 'Rejected';

                  if (isSubmitted) {
                    return (
                      <Alert variant={myAssignment.status === 'Approved' ? 'success' : 'primary'} className="mb-0 p-4 text-center border-0 shadow-sm rounded-4">
                        <div className={`mb-3 p-3 rounded-circle bg-white shadow-sm d-inline-flex align-items-center justify-content-center mx-auto`} style={{ width: '60px', height: '60px' }}>
                          <i className={`fa-solid ${myAssignment.status === 'Approved' ? 'fa-check-double text-success' : 'fa-hourglass-half text-primary'} fs-3`}></i>
                        </div>
                        <h6 className="fw-bold mt-2 mb-1 text-uppercase">Task {myAssignment.status}</h6>
                        <p className="small mb-0 opacity-75">
                          {myAssignment.status === 'Submitted' ? 'Admin will review your submission soon.' : 'Great job! Your submission was accepted.'}
                        </p>
                        {myAssignment.file_id && (
                          <div className="mt-3 p-2 bg-white bg-opacity-50 rounded-3 d-flex align-items-center justify-content-between border-0">
                            <div className="d-flex align-items-center overflow-hidden">
                              <i className="fa-solid fa-paperclip text-dark me-2 ms-1"></i>
                              <span className="text-dark small text-truncate fw-medium">{myAssignment.file_title || 'Your Attachment'}</span>
                            </div>
                            <Button variant="link" size="sm" className="p-0 text-dark fw-bold text-decoration-none" onClick={() => handleDownload(myAssignment.file_id, myAssignment.file_title)}>
                              <i className="fa-solid fa-download"></i>
                            </Button>
                          </div>
                        )}
                        {myAssignment.admin_comment && (
                          <div className="mt-3 p-3 bg-white bg-opacity-50 rounded-3 border-0 small text-start">
                            <strong className="d-block mb-1 text-dark"><i className="fa-solid fa-comment-dots me-1"></i> ADMIN FEEDBACK:</strong>
                            <span className="text-dark opacity-75">{myAssignment.admin_comment}</span>
                          </div>
                        )}
                      </Alert>
                    );
                  }

                  return (
                    <div className="mt-2">
                      {isCancelled && (
                        <Alert variant="secondary" className="mb-3 border-0 rounded-3 p-3 shadow-sm small text-center">
                          <div className="d-flex align-items-center justify-content-center mb-1 fw-bold text-secondary">
                            <i className="fa-solid fa-ban me-2"></i> TASK CANCELLED
                          </div>
                          <div className="opacity-75">This task has been cancelled. Submissions are disabled.</div>
                        </Alert>
                      )}

                      {isRejected && !isCancelled && (
                        <Alert variant="danger" className="mb-3 border-0 rounded-3 p-3 shadow-sm small">
                          <div className="d-flex align-items-center mb-2 fw-bold">
                            <i className="fa-solid fa-circle-exclamation me-2"></i> TASK REJECTED
                          </div>
                          {myAssignment.admin_comment && (
                            <div className="p-2 bg-white bg-opacity-50 rounded-2 border-0 text-dark mb-1">
                              <strong className="d-block small text-danger"><i className="fa-solid fa-comment-dots me-1"></i> ADMIN REASON:</strong>
                              {myAssignment.admin_comment}
                            </div>
                          )}
                          <div className="opacity-75 mt-1">Please re-submit your work below.</div>
                        </Alert>
                      )}
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-muted">SUBMISSION NOTE</Form.Label>
                        <div className="border rounded-3 bg-white overflow-hidden shadow-sm" style={isCancelled ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
                          <EditorProvider>
                            <Editor
                              value={submissionDescription}
                              onChange={(e) => !isCancelled && setSubmissionDescription(e.target.value)}
                              style={{ minHeight: '150px', border: 'none' }}
                            >
                              <Toolbar>
                                <BtnBold /><BtnItalic /><BtnUnderline /><Separator />
                                <BtnNumberedList /><BtnBulletList /><Separator />
                                <BtnClearFormatting />
                              </Toolbar>
                            </Editor>
                          </EditorProvider>
                        </div>
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-muted">UPLOAD FILE {isRejected && !isCancelled && <Badge bg="danger" className="ms-2">RE-SUBMIT</Badge>}</Form.Label>
                        <Form.Control 
                          type="file" 
                          className="bg-light border-0 shadow-sm"
                          onChange={(e) => setFile(e.target.files[0])} 
                          disabled={isCancelled}
                        />
                      </Form.Group>
                      
                      <Button 
                        variant={isCancelled ? "secondary" : "primary"} 
                        className="w-100 py-3 rounded-3 shadow fw-bold mt-2" 
                        onClick={handleTeacherSubmit} 
                        disabled={loading || isCancelled}
                      >
                        <i className={`fa-solid ${isCancelled ? 'fa-ban' : 'fa-paper-plane'} me-2`}></i>
                        {isCancelled ? 'TASK CANCELLED' : (isRejected ? 'RE-SUBMIT TASK' : 'SUBMIT TASK')}
                      </Button>
                    </div>
                  );
                })()}
              </div>

              {isAdmin && (
                <div className="mt-4 pt-2">
                  <h6 className="fw-bold text-muted mb-3 border-bottom pb-2 text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>Global Controls</h6>
                  <div className="d-grid gap-2">
                    <Button variant="outline-info" size="sm" className="rounded-3 py-2 fw-bold" onClick={() => handleGlobalStatusUpdate('In Progress')} disabled={task.status === 'In Progress'}>
                      <i className="fa-solid fa-play me-2"></i>In Progress
                    </Button>
                    <Button variant="outline-success" size="sm" className="rounded-3 py-2 fw-bold" onClick={() => handleGlobalStatusUpdate('Completed')} disabled={task.status === 'Completed'}>
                      <i className="fa-solid fa-check-circle me-2"></i>Complete Task
                    </Button>
                    <Button variant="outline-secondary" size="sm" className="rounded-3 py-2 fw-bold" onClick={() => handleGlobalStatusUpdate('Cancelled')} disabled={task.status === 'Cancelled'}>
                      <i className="fa-solid fa-ban me-2"></i>Cancel Task
                    </Button>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {isAdmin && (
        <Card className="shadow-sm border-0 rounded-4 overflow-hidden">
          <Card.Header className="bg-white py-3 border-bottom border-light">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
              <i className="fa-solid fa-users-viewfinder me-2 text-primary"></i>Assignees Progress
            </h5>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 custom-task-table">
                <thead className="bg-light text-uppercase small text-muted">
                  <tr>
                    <th className="ps-4 py-3" style={{ width: '25%' }}>User</th>
                    <th style={{ width: '15%' }}>Status</th>
                    <th style={{ width: '45%' }}>Submission Summary</th>
                    <th className="text-center py-3" style={{ width: '15%' }}>Action</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {(() => {
                    let users = task.assigned_users;
                    if (typeof users === 'string') {
                      try { users = JSON.parse(users); } catch (e) { users = []; }
                    }
                    if (!users || users.length === 0) {
                      return <tr><td colSpan="4" className="text-center py-5 text-muted italic">No assignees found for this task.</td></tr>;
                    }
                    return users.map((user, idx) => (
                      <tr key={idx} className="user-row" onClick={() => {
                        setSelectedUserForReview(user);
                        setReviewModalShow(true);
                      }} style={{ cursor: 'pointer', transition: 'all 0.2s' }}>
                        <td className="ps-4">
                          <div className="d-flex align-items-center">
                            <div className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center me-3 fw-bold" style={{ width: '35px', height: '35px', fontSize: '0.8rem' }}>
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="fw-bold text-dark">{user.name}</span>
                          </div>
                        </td>
                        <td>
                          <Badge bg={statusColors[user.status] || 'secondary'} className="px-3 py-2 rounded-pill fw-normal" style={{ fontSize: '0.7rem' }}>
                            {user.status?.toUpperCase() || 'PENDING'}
                          </Badge>
                        </td>
                        <td>
                          <div className="text-muted small text-truncate" style={{ maxWidth: '400px' }} dangerouslySetInnerHTML={{ __html: user.description || '<span class="opacity-50 italic">No note submitted yet</span>' }}></div>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Button size="sm" variant="light" className="rounded-pill px-3 shadow-sm border small fw-bold text-primary">
                              REVIEW
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}

      {reviewModalShow && (
        <ReviewTaskModal
          show={reviewModalShow}
          onHide={() => setReviewModalShow(false)}
          user={selectedUserForReview}
          onApprove={(userId, adminComment) => {
            handleIndividualStatusUpdate(userId, 'Approved', adminComment);
            setReviewModalShow(false);
          }}
          onReject={(userId, adminComment) => {
            handleIndividualStatusUpdate(userId, 'Rejected', adminComment);
            setReviewModalShow(false);
          }}
          onDownload={handleDownload}
        />
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05); }
        .user-row:hover { background-color: #f8f9ff !important; transform: translateX(5px); }
        .action-icon-btn { width: 32px; height: 32px; padding: 0; display: flex; align-items: center; justify-content: center; }
        .custom-task-table th { border-bottom: none; }
        .custom-task-table td { border-top: 1px solid #f0f0f0; border-bottom: none; }
        .border-dashed { border: 2px dashed #eee; }
      `}} />
    </Container>
  );
};

export default TaskDetailView;
